import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAssessmentStatus } from "@/hooks/useAssessmentStatus";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Home, FileText, TrendingUp, RefreshCw, Play, Eye } from "lucide-react";
import { questions } from "@/data/questions";
import { useToast } from "@/hooks/use-toast";

interface UserAttempt {
  id: string;
  run_id: string;
  completed: boolean;
  overall_score: number | null;
  started_at: string;
  completed_at: string | null;
  assessment_runs: {
    name: string;
  } | null;
  responses: Array<{
    question_id: string;
    rating: number;
    competence_area: string;
  }>;
}

const UserDashboard = () => {
  const { user, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeRun, latestAttempt, loading: statusLoading, refreshStatus } = useAssessmentStatus();

  const [attempts, setAttempts] = useState<UserAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState<UserAttempt | null>(null);
  const [responses, setResponses] = useState<any[]>([]);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchAttempts();
    }
  }, [user]);

  // fetchActiveRun removed - using hook instead

  const fetchAttempts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("attempts")
      .select("*")
      .eq("user_id", user?.id)
      .order("started_at", { ascending: false });

    if (data) {
      const attemptsWithDetails = await Promise.all(
        data.map(async (attempt) => {
          const { data: run } = await supabase
            .from("assessment_runs")
            .select("name")
            .eq("id", attempt.run_id)
            .single();

          const { data: responses } = await supabase
            .from("responses")
            .select("question_id, rating, competence_area")
            .eq("attempt_id", attempt.id);

          return {
            ...attempt,
            assessment_runs: run,
            responses: responses || [],
          };
        })
      );
      setAttempts(attemptsWithDetails);
    }
    setLoading(false);
  };

  const viewResponses = async (attempt: UserAttempt) => {
    setSelectedAttempt(attempt);
    const { data } = await supabase
      .from("responses")
      .select("*")
      .eq("attempt_id", attempt.id)
      .order("created_at", { ascending: true });

    if (data) {
      setResponses(data);
    }
    setShowResponseDialog(true);
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return "text-muted-foreground";
    if (score >= 80) return "text-success";
    if (score >= 60) return "text-primary";
    if (score >= 40) return "text-accent";
    return "text-muted-foreground";
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Bilingual Instructions & Actions */}
        <Card className="p-8 mb-6 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-3">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Instructions:</strong> View your assessment history, track your progress, and access detailed reports for each completed assessment.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">التعليمات:</strong> عرض سجل التقييمات الخاصة بك، تتبع تقدمك، والوصول إلى التقارير التفصيلية لكل تقييم مكتمل.
            </p>
          </div>
        </Card>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-foreground">My Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Welcome back, {profile?.first_name || user?.email}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Smart Action Buttons */}
            {!statusLoading && activeRun && (
              <>
                {(!latestAttempt || (!latestAttempt.completed && latestAttempt.responseCount === 0)) ? (
                  <Button size="lg" onClick={() => navigate('/assessment')} className="gap-2 shadow-sm">
                    <Play className="h-5 w-5" />
                    Start New Assessment
                  </Button>
                ) : !latestAttempt.completed ? (
                  <>
                    <Button size="lg" onClick={() => navigate('/assessment')} className="gap-2 bg-green-600 hover:bg-green-700 text-white shadow-sm">
                      <Play className="h-5 w-5" />
                      Continue
                    </Button>


                  </>
                ) : null}
              </>
            )}

            <Button variant="outline" onClick={() => navigate("/")} className="gap-2 h-11">
              <Home className="h-4 w-4" />
              Home
            </Button>
            {attempts.length > 0 && attempts.some(a => a.completed) && (
              <Button
                variant="outline"
                onClick={() => {
                  const latestCompleted = attempts.find(a => a.completed);
                  if (latestCompleted) viewResponses(latestCompleted);
                }}
                className="gap-2 h-11"
              >
                <Eye className="h-4 w-4" />
                View Responses
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/analytics")} className="gap-2 h-11">
              <TrendingUp className="h-4 w-4" />
              Analytics
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/20 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Attempts</p>
                <p className="text-2xl font-bold text-foreground">{attempts.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/20 rounded-lg">
                <TrendingUp className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-foreground">
                  {attempts.filter((a) => a.completed).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/20 rounded-lg">
                <FileText className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Average Score</p>
                <p className="text-2xl font-bold text-foreground">
                  {attempts.length > 0
                    ? Math.round(
                      attempts
                        .filter((a) => a.overall_score)
                        .reduce((sum, a) => sum + (a.overall_score || 0), 0) /
                      attempts.filter((a) => a.overall_score).length
                    )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Detailed Scores - النتائج التفصيلية */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Detailed Scores - النتائج التفصيلية</h2>
          {attempts.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Area - المجال</TableHead>
                  <TableHead>Score - النتيجة</TableHead>
                  <TableHead>Questions - الأسئلة</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const areas = [
                    "Information & Data Literacy",
                    "Communication & Collaboration",
                    "Digital Content Creation",
                    "Safety",
                    "Problem Solving"
                  ];
                  // Use the latest completed attempt for details
                  const latestAttempt = attempts.find(a => a.completed) || attempts[0];
                  console.log('[Dashboard Debug] Latest attempt:', latestAttempt?.id);
                  console.log('[Dashboard Debug] Total responses:', latestAttempt?.responses?.length);
                  return areas.map(area => {
                    const areaResponses = latestAttempt.responses.filter(r => r.competence_area === area);
                    console.log(`[Dashboard Debug] ${area}: ${areaResponses.length} responses`);
                    const numQuestions = areaResponses.length;
                    // Calculate area score
                    const totalRating = areaResponses.reduce((sum, r) => sum + r.rating, 0);
                    const maxScore = numQuestions * 8;
                    const minScore = numQuestions * 1;
                    const score = numQuestions > 0 ? ((totalRating - minScore) / (maxScore - minScore)) * 100 : 0;
                    return (
                      <TableRow key={area}>
                        <TableCell>{area}</TableCell>
                        <TableCell>{score ? score.toFixed(1) + "%" : "-"}</TableCell>
                        <TableCell>{numQuestions}</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          )}
        </Card>
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Assessment History</h2>

          {/* Active Assessment Action Area */}
          {activeRun && (
            <div className="mb-6 p-4 bg-muted/20 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Current Assessment: {activeRun.name}</h3>
              <div className="flex flex-wrap gap-4">
                {(() => {
                  // Find all attempts for the current run
                  const runAttempts = attempts.filter(a => a.run_id === activeRun.id);
                  // If there are no attempts for this run, show Start New Assessment
                  if (runAttempts.length === 0) {
                    return (
                      <Button onClick={() => navigate("/assessment")}>Start New Assessment</Button>
                    );
                  }
                  // If there is an incomplete attempt, show Continue/Restart
                  if (runAttempts.some(a => !a.completed)) {
                    return (
                      <>
                        <Button
                          onClick={() => navigate("/assessment")}
                          className="gap-2 bg-primary hover:bg-primary/90"
                        >
                          <FileText className="h-4 w-4" />
                          Continue Assessment
                        </Button>


                      </>
                    );
                  }
                  // If all attempts for this run are completed, show nothing
                  return null;
                })()}
              </div>
            </div>
          )}

          {attempts.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">
                No past assessment history found.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assessment Run</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attempts.map((attempt) => (
                    <TableRow key={attempt.id}>
                      <TableCell className="font-medium">
                        {attempt.assessment_runs?.name || "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${attempt.completed
                            ? "bg-success/20 text-success"
                            : "bg-accent/20 text-accent"
                            }`}
                        >
                          {attempt.completed ? "Completed" : "In Progress"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {attempt.overall_score ? (
                          <span
                            className={`text-lg font-bold ${getScoreColor(
                              attempt.overall_score
                            )}`}
                          >
                            {Math.round(attempt.overall_score)}%
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {attempt.responses?.length ?? 0}
                      </TableCell>
                      <TableCell>
                        {new Date(attempt.started_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {attempt.completed_at
                          ? new Date(attempt.completed_at).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewResponses(attempt)}
                          className="gap-2"
                          disabled={!attempt.completed}
                        >
                          <Eye className="h-4 w-4" />
                          View Responses
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                My Responses
              </DialogTitle>
              {selectedAttempt && (
                <div className="text-sm text-muted-foreground">
                  {selectedAttempt.assessment_runs?.name || "N/A"}
                </div>
              )}
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {responses.map((response) => {
                const question = questions.find((q) => q.id.toString() === response.question_id);
                return (
                  <Card key={response.question_id} className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-foreground mb-1">
                            {question?.question || response.question_id}
                          </p>
                          {question?.questionAr && (
                            <p className="text-sm text-muted-foreground font-tajawal" dir="rtl">
                              {question.questionAr}
                            </p>
                          )}
                        </div>
                        <div className="ml-4">
                          <span className="px-3 py-1 bg-primary/20 text-primary rounded-full font-bold">
                            {response.rating}/8
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-semibold">Area:</span> {response.competence_area}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UserDashboard;
